import { useCallback, useEffect, useState } from 'react';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { CatalogMergeModal } from '../features/riskCatalog/CatalogMergeModal';
import {
  createCatalogItem,
  fetchCatalogItems,
  mergeCatalogItems,
  updateCatalogItem,
} from '../features/riskCatalog/riskCatalogApi';
import { CATALOG_KIND_LABEL, type RiskCatalogItem, type RiskCatalogKind } from '../features/riskCatalog/types';

const KINDS: RiskCatalogKind[] = ['area_gestion', 'modo_falla', 'efecto', 'control', 'situacion_op'];

export function RiskCatalogPage() {
  const { canEdit } = usePlatformAccessLevel('risk_catalog.access');
  const [kind, setKind] = useState<RiskCatalogKind>('area_gestion');
  const [items, setItems] = useState<RiskCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [mergeSource, setMergeSource] = useState<RiskCatalogItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCatalogItems(kind);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    await createCatalogItem({ kind, label: newLabel.trim() });
    setNewLabel('');
    await load();
  };

  const handleToggle = async (item: RiskCatalogItem) => {
    await updateCatalogItem(item.id, { active: !item.active });
    await load();
  };

  const handleMerge = async (toId: string) => {
    if (!mergeSource) return;
    await mergeCatalogItems(mergeSource.id, toId);
    await load();
  };

  return (
    <div className='mx-auto max-w-3xl p-6'>
      <h1 className='mb-2 text-2xl font-bold'>Catálogos de riesgo</h1>
      <p className='mb-6 text-sm text-gray-600'>ABM de modos de falla, efectos y áreas (admin)</p>

      <div className='mb-4 flex flex-wrap gap-2'>
        {KINDS.map(k => (
          <button
            key={k}
            type='button'
            className={`rounded px-3 py-1 text-sm ${kind === k ? 'bg-blue-600 text-white' : 'border'}`}
            onClick={() => setKind(k)}
          >
            {CATALOG_KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {canEdit && (
        <form onSubmit={handleCreate} className='mb-4 flex gap-2'>
          <input
            className='flex-1 rounded border px-3 py-2'
            placeholder={`Nuevo ítem — ${CATALOG_KIND_LABEL[kind]}`}
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            maxLength={300}
          />
          <button type='submit' className='rounded bg-blue-600 px-4 py-2 text-white'>
            Agregar
          </button>
        </form>
      )}

      {loading ? (
        <p>Cargando…</p>
      ) : (
        <ul className='divide-y rounded border'>
          {items.map(item => (
            <li key={item.id} className='flex items-center justify-between px-4 py-2 text-sm'>
              <span className={item.active ? '' : 'text-gray-400 line-through'}>{item.label}</span>
              {canEdit && (
                <div className='flex gap-2'>
                  <button type='button' className='text-gray-600' onClick={() => void handleToggle(item)}>
                    {item.active ? 'Desactivar' : 'Activar'}
                  </button>
                  {(kind === 'modo_falla' || kind === 'efecto' || kind === 'area_gestion') && (
                    <button
                      type='button'
                      className='text-blue-600'
                      onClick={() => setMergeSource(item)}
                    >
                      Fusionar
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <CatalogMergeModal
        open={mergeSource !== null}
        onOpenChange={(open) => {
          if (!open) setMergeSource(null);
        }}
        sourceItem={mergeSource}
        candidates={items}
        onSubmit={handleMerge}
      />
    </div>
  );
}
